import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, Moon, Sun, Zap, Loader2, TrendingUp, AlertCircle, Flame, Droplets, Brain, Users, HelpCircle } from "lucide-react";
import { format } from "date-fns";

const WAKE_REASONS = [
  { id: 'hot_flash', label: 'Hot Flash', icon: Flame },
  { id: 'bathroom', label: 'Bathroom', icon: Droplets },
  { id: 'anxiety', label: 'Anxiety', icon: Brain },
  { id: 'pain', label: 'Pain', icon: Zap },
  { id: 'partner', label: 'Partner/Noise', icon: Users },
  { id: 'other', label: 'Other', icon: HelpCircle },
];

type IconComponent = React.ComponentType<{ className?: string }>;

interface SleepInsights {
  top_disruptor: string;
  key_insight: string;
  correlation: string;
  quick_win: string;
  progress_note: string;
}

export default function SleepTracker() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [insights, setInsights] = useState<SleepInsights | null>(null);
  
  const [logDate, setLogDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [bedtime, setBedtime] = useState('22:00');
  const [wakeTime, setWakeTime] = useState('07:00');
  const [sleepQuality, setSleepQuality] = useState(3);
  const [morningEnergy, setMorningEnergy] = useState(3);
  const [timesWoken, setTimesWoken] = useState(0);
  const [wakeReasons, setWakeReasons] = useState<string[]>([]);
  const [roomTemp, setRoomTemp] = useState('');
  const [tempUnit, setTempUnit] = useState<'F' | 'C'>('F');
  const [notes, setNotes] = useState('');
  const [whatHelped, setWhatHelped] = useState('');

  useEffect(() => {
    const initAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/auth');
        return;
      }
      // Only fetch insights if authenticated
      fetchInsights();
    };
    initAuth();
  }, []);

  const fetchInsights = async () => {
    setAnalyzing(true);
    try {
      // Double-check auth before calling edge function
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('User not authenticated, skipping insights fetch');
        return;
      }

      const { data, error } = await supabase.functions.invoke('analyze-sleep-insights', {
        body: { days: 30 }
      });

      if (error) throw error;
      if (data?.insights) {
        setInsights(data.insights);
      }
    } catch (error: any) {
      console.error('Error fetching insights:', error);
      // Don't show error toast, just log it
    } finally {
      setAnalyzing(false);
    }
  };

  const calculateTotalHours = (): number => {
    if (!bedtime || !wakeTime) return 0;
    const [bedHour, bedMin] = bedtime.split(':').map(Number);
    const [wakeHour, wakeMin] = wakeTime.split(':').map(Number);
    
    let bedMinutes = bedHour * 60 + bedMin;
    let wakeMinutes = wakeHour * 60 + wakeMin;
    
    if (wakeMinutes < bedMinutes) wakeMinutes += 24 * 60;
    
    return parseFloat(((wakeMinutes - bedMinutes) / 60).toFixed(1));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase.from('sleep_logs').insert({
        user_id: user.id,
        log_date: logDate,
        bedtime,
        wake_time: wakeTime,
        total_hours: calculateTotalHours(),
        sleep_quality: sleepQuality,
        morning_energy: morningEnergy,
        times_woken: timesWoken,
        wake_reasons: wakeReasons,
        room_temp: roomTemp ? parseInt(roomTemp) : null,
        notes: notes || null,
        what_helped: whatHelped || null,
      });

      if (error) throw error;

      toast.success('Sleep log saved!');
      
      // Refresh insights after logging
      fetchInsights();
      
      // Reset form
      setLogDate(format(new Date(), 'yyyy-MM-dd'));
      setTimesWoken(0);
      setWakeReasons([]);
      setNotes('');
      setWhatHelped('');
      
    } catch (error: any) {
      console.error('Error saving sleep log:', error);
      toast.error(error.message || 'Failed to save sleep log');
    } finally {
      setLoading(false);
    }
  };

  const toggleWakeReason = (reason: string) => {
    setWakeReasons(prev =>
      prev.includes(reason) ? prev.filter(r => r !== reason) : [...prev, reason]
    );
  };

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Button
          variant="ghost"
          onClick={() => navigate('/tracker')}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Tracker
        </Button>

        {/* AI Insights Card */}
        {analyzing ? (
          <Card className="mb-8 border-primary/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-center gap-3 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                <p>Analyzing your sleep patterns...</p>
              </div>
            </CardContent>
          </Card>
        ) : insights ? (
          <Card className="mb-8 border-primary/30 bg-gradient-to-br from-primary/5 to-accent/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-headline text-2xl">
                <Zap className="h-6 w-6 text-primary" />
                Your Sleep Insights
              </CardTitle>
              <CardDescription>AI-powered analysis of your sleep patterns</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3 p-3 bg-background/60 rounded-lg">
                <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-sm text-muted-foreground">Top Sleep Disruptor</p>
                  <p className="text-foreground">{insights.top_disruptor.replace('_', ' ').toUpperCase()}</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-background/60 rounded-lg">
                <TrendingUp className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-sm text-muted-foreground">Key Finding</p>
                  <p className="text-foreground">{insights.key_insight}</p>
                </div>
              </div>

              <div className="p-3 bg-accent/20 rounded-lg border border-accent/30">
                <p className="font-semibold text-sm text-muted-foreground mb-1">Pattern Discovered</p>
                <p className="text-foreground">{insights.correlation}</p>
              </div>

              <div className="p-3 bg-primary/10 rounded-lg border border-primary/30">
                <p className="font-semibold text-sm text-primary mb-1">💡 Quick Win - Try Tonight</p>
                <p className="text-foreground">{insights.quick_win}</p>
              </div>

              <p className="text-sm text-muted-foreground italic border-l-2 border-primary/50 pl-3">
                {insights.progress_note}
              </p>
            </CardContent>
          </Card>
        ) : null}

        {/* Sleep Log Form */}
        <Card>
          <CardHeader>
            <CardTitle className="font-headline text-3xl flex items-center gap-2">
              <Moon className="h-7 w-7" />
              Log Your Sleep
            </CardTitle>
            <CardDescription>Track what's affecting your rest</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="log_date">Date</Label>
                  <Input
                    id="log_date"
                    type="date"
                    value={logDate}
                    onChange={(e) => setLogDate(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="bedtime">Bedtime</Label>
                  <Input
                    id="bedtime"
                    type="time"
                    value={bedtime}
                    onChange={(e) => setBedtime(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="wake_time">Wake Time</Label>
                  <Input
                    id="wake_time"
                    type="time"
                    value={wakeTime}
                    onChange={(e) => setWakeTime(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Sleep Quality: {sleepQuality}/5</Label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={sleepQuality}
                    onChange={(e) => setSleepQuality(parseInt(e.target.value))}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground mt-1">1 = Terrible, 5 = Excellent</p>
                </div>
                <div>
                  <Label>Morning Energy: {morningEnergy}/5</Label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={morningEnergy}
                    onChange={(e) => setMorningEnergy(parseInt(e.target.value))}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground mt-1">1 = Exhausted, 5 = Energized</p>
                </div>
              </div>

              <div>
                <Label htmlFor="times_woken">Times Woken Up</Label>
                <Input
                  id="times_woken"
                  type="number"
                  min="0"
                  value={timesWoken.toString()}
                  onChange={(e) => setTimesWoken(parseInt(e.target.value) || 0)}
                />
              </div>

              <div>
                <Label className="mb-3 block">Why did you wake up?</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {WAKE_REASONS.map((reason) => (
                    <div key={reason.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={reason.id}
                        checked={wakeReasons.includes(reason.id)}
                        onCheckedChange={() => toggleWakeReason(reason.id)}
                      />
                      <label
                        htmlFor={reason.id}
                        className="text-sm cursor-pointer flex items-center gap-1"
                      >
                        <reason.icon className="h-4 w-4 text-primary/70" />
                        {reason.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="room_temp">Room Temperature (Optional)</Label>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant={tempUnit === 'F' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setTempUnit('F')}
                      className="h-7 px-2 text-xs"
                    >
                      °F
                    </Button>
                    <Button
                      type="button"
                      variant={tempUnit === 'C' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setTempUnit('C')}
                      className="h-7 px-2 text-xs"
                    >
                      °C
                    </Button>
                  </div>
                </div>
                <Input
                  id="room_temp"
                  type="number"
                  placeholder={tempUnit === 'F' ? 'e.g., 68' : 'e.g., 20'}
                  value={roomTemp}
                  onChange={(e) => setRoomTemp(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="what_helped">What Helped Tonight?</Label>
                <Textarea
                  id="what_helped"
                  placeholder="e.g., Used cooling sheets, did breathing exercises..."
                  value={whatHelped}
                  onChange={(e) => setWhatHelped(e.target.value)}
                  rows={2}
                />
              </div>

              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  placeholder="Anything else about your sleep..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <Sun className="inline h-4 w-4 mr-1" />
                  Total Hours: <span className="font-semibold text-foreground">{calculateTotalHours().toFixed(1)}h</span>
                </p>
              </div>

              <Button type="submit" disabled={loading} className="w-full" size="lg">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Sleep Log
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}